import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FileUploadType } from '@shared/enums';
import { ToastNotificationService } from '@shared/services';
import { PersistenceService } from '@shared/services/persistence';
import { catchError, EMPTY, mergeMap, Observable, of, tap } from 'rxjs';
import { FileSaverService } from '.';

@UntilDestroy()
@Injectable()
export class FileService {
  constructor(
    private persistenceService: PersistenceService,
    private toastNotificationService: ToastNotificationService,
    private fileSaverService: FileSaverService
  ) {}

  uploadFile(
    file: File,
    fileType: FileUploadType,
    clientId: string,
    publiclyAvailable = false
  ): Observable<{ url: string; thumbnailUrl: string; file?: File }> {
    const toastId = this.toastNotificationService.info(
      $localize`:@@shared.fileService.txtUploading:Uploading ${file.name}:fileName:`,
      true
    );

    const formData = new FormData();
    formData.append('file', file, file.name);

    const uploadConfig = {
      directory: '',
      publiclyAvailable,
      generateThumbnail: false,
    };

    switch (fileType) {
      case FileUploadType.Attachment:
      case FileUploadType.Report:
      case FileUploadType.UserManual:
      case FileUploadType.Logo:
        uploadConfig.directory = 'dpr-module';
        break;

      default:
        throw new Error(
          'Unknown file type. Please provide valid file type name.'
        );
    }
    uploadConfig.directory = `${uploadConfig.directory}/${clientId}`;

    return this.persistenceService.uploadFile(formData, uploadConfig).pipe(
      mergeMap((res: string) => {
        const response: { url: string; thumbnailUrl: string; file?: File } =
          JSON.parse(res);
        this.toastNotificationService.hide(toastId);
        this.toastNotificationService.success(
          $localize`:@@shared.fileService.txtUploaded:Uploaded ${file.name}:fileName:`
        );

        response.file = file;
        return of(response);
      })
    );
  }

  download(
    externalUrl: string,
    fileName: string,
    fileType: string,
    existingToastId?: number
  ): void {
    const toastId =
      existingToastId ??
      this.toastNotificationService.info(
        $localize`:@@shared.fileService.txtDownloading:Downloading ${fileName}:fileName:`,
        true
      );

    this.persistenceService
      .downloadFile(externalUrl, fileName)
      .pipe(
        untilDestroyed(this),
        tap((blob) => {
          this.fileSaverService.save(blob, fileName, fileType, toastId);
        }),
        catchError((error) => {
          this.toastNotificationService.hide(toastId);
          this.toastNotificationService.error(
            $localize`:@@shared.fileService.txtErrorDownloading:Error downloading ${fileName}:fileName:`
          );
          console.error(
            `Error downloading the file ${fileName}`,
            error.message
          );
          return EMPTY;
        })
      )
      .subscribe();
  }
}
